// Shared pieces for the Platform Legal & Compliance screens.
//
// Mobile-first like the rest of the admin area: tables scroll inside their
// own container, forms stack to one column at 375px, and no element sets a
// min-width wider than the viewport.
//
// Deliberately separate from components/finance/shared.jsx. The two look
// alike on purpose, but Finance & Books is frozen and its atoms carry
// money-specific formatting; a compliance status pill has different rules
// (unknown reads as outstanding, never as neutral).
import { Loader2 } from "lucide-react";

import {
  ALERT_TONES, EXTERNAL_ACTION_NOTE, SEVERITY_TONES, clockLabel, clockTone, humanise,
  statusLabel, statusTone,
} from "../../lib/platformLegal";

export function Section({ title, description, actions, children, testId, className = "" }) {
  return (
    <section className={`rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 ${className}`}
             data-testid={testId}>
      {(title || actions) && (
        <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            {title && <h2 className="font-display text-lg font-semibold text-slate-900">{title}</h2>}
            {description && (
              <p className="text-sm text-slate-600 mt-0.5 max-w-3xl">{description}</p>
            )}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function StatusPill({ status, children }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium
                      ${statusTone(status)}`}
          data-testid={`status-${status}`}>
      {children || statusLabel(status)}
    </span>
  );
}

export function TonePill({ tone, children, testId }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${tone}`}
          data-testid={testId}>
      {children}
    </span>
  );
}

export function SeverityPill({ severity }) {
  return <TonePill tone={SEVERITY_TONES[severity] || SEVERITY_TONES.normal}>
    {humanise(severity)}
  </TonePill>;
}

export function ClockPill({ clock }) {
  if (!clock) return null;
  return <TonePill tone={clockTone(clock.state)}>{clockLabel(clock)}</TonePill>;
}

export function Stat({ label, value, hint, tone = "text-slate-900", testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={testId}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">
        {label}
      </div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${tone}`}>{value}</div>
      {hint && <div className="mt-0.5 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

export function Table({ head, children, testId, minWidth = 560 }) {
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm" style={{ minWidth }}>
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-slate-500
                         border-b border-slate-200">
            {head.map((h) => (
              <th key={h.key || h}
                  className={`py-2 pr-3 font-semibold ${h.align === "right" ? "text-right" : ""}`}>
                {h.label || h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table>
    </div>
  );
}

export function Empty({ children, testId }) {
  return <p className="py-6 text-center text-sm text-slate-500" data-testid={testId}>{children}</p>;
}

export function Loading({ label = "Loading..." }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500"
         data-testid="pl-loading" role="status">
      <Loader2 className="w-4 h-4 animate-spin" /> {label}
    </div>
  );
}

export function Field({ label, children, hint, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
        {label}
      </span>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </label>
  );
}

export const inputClass =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none";

export function Button({ children, variant = "primary", className = "", busy, ...rest }) {
  const styles = {
    primary: "bg-slate-900 text-white hover:bg-slate-800",
    secondary: "border border-slate-300 bg-white text-slate-800 hover:bg-slate-50",
    danger: "border border-rose-300 bg-white text-rose-700 hover:bg-rose-50",
    ghost: "text-slate-600 hover:text-slate-900",
  }[variant];
  return (
    <button {...rest} disabled={rest.disabled || busy}
            className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm
                        font-medium disabled:opacity-50 disabled:cursor-not-allowed ${styles} ${className}`}>
      {busy && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
}

export function Modal({ title, description, onClose, children, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 sm:p-4"
         role="dialog" aria-modal="true" aria-label={title} data-testid={testId}>
      <div className="w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl
                      bg-white p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display text-lg font-semibold">{title}</h3>
            {description && <p className="text-sm text-slate-600 mt-0.5">{description}</p>}
          </div>
          <button onClick={onClose} aria-label="Close"
                  className="shrink-0 text-slate-400 hover:text-slate-700 text-sm">
            Close
          </button>
        </div>
        <div className="mt-4 space-y-3">{children}</div>
      </div>
    </div>
  );
}

/** The marker every item that needs a real-world legal act carries. */
export function ExternalActionNote({ children }) {
  return (
    <p className="mt-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs
                  text-violet-900" data-testid="external-action-note">
      <span className="font-semibold">{EXTERNAL_ACTION_NOTE}.</span>{" "}
      {children || "ConfirmPH records that this happened; it cannot do it."}
    </p>
  );
}

/** A plain, honest note. Used wherever the product has to say what it is
 *  NOT doing — no automatic notification, no liability finding, no
 *  automated deletion. */
export function Note({ children, tone = "slate", testId }) {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
    sky: "border-sky-200 bg-sky-50 text-sky-900",
  };
  return (
    <p className={`rounded-lg border px-3 py-2 text-xs ${tones[tone]}`} data-testid={testId}>
      {children}
    </p>
  );
}

export function AlertRow({ alert, onOpen }) {
  return (
    <button onClick={() => onOpen?.(alert.link)}
            className={`w-full text-left rounded-xl border px-3 py-2 ${ALERT_TONES[alert.severity]}`}
            data-testid={`pl-alert-${alert.key}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-semibold">{alert.title}</span>
        <span className="text-xs font-semibold tabular-nums">{alert.count}</span>
      </div>
      <p className="mt-0.5 text-xs opacity-90">{alert.detail}</p>
    </button>
  );
}

export function EvidenceList({ refs, onDownload }) {
  if (!refs?.length) {
    return <span className="text-xs text-slate-500">No evidence attached</span>;
  }
  return (
    <ul className="flex flex-wrap gap-1.5">
      {refs.map((ref) => (
        <li key={`${ref.type}-${ref.id}`}>
          <button onClick={() => onDownload?.(ref)}
                  className="rounded-full border border-slate-300 px-2 py-0.5 text-xs text-slate-700
                             hover:bg-slate-50">
            {ref.label || humanise(ref.type)}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function Tabs({ tabs, active, onChange, testId }) {
  return (
    <div className="overflow-x-auto">
      <div className="flex gap-1.5 min-w-max" role="tablist" data-testid={testId}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const on = active === tab.key;
          return (
            <button key={tab.key} role="tab" aria-selected={on}
                    onClick={() => onChange(tab.key)}
                    data-testid={`${testId}-${tab.key}`}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium
                                border whitespace-nowrap ${on
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
              {Icon && <Icon className="w-4 h-4" />} {tab.label}
              {tab.badge ? (
                <span className="ml-0.5 rounded-full bg-rose-600 px-1.5 text-[10px] font-semibold text-white">
                  {tab.badge}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
